import { useState } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PiggyBank, ClipboardList, Search, ExternalLink } from 'lucide-react'
import { useSarkariSchemes, useSarkariKaam } from '@/hooks/use-supabase-data'

type TabKey = 'schemes' | 'kaam'

const CATEGORIES: { key: string | null; label: string }[] = [
  { key: null, label: 'All' },
  { key: 'government', label: 'Government' },
  { key: 'women', label: 'Women' },
  { key: 'farmer', label: 'Farmer' },
]

const CATEGORY_STYLE: Record<string, string> = {
  women: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  farmer: 'bg-green-500/10 text-green-600 dark:text-green-400',
  government: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
}

export default function SchemesSewa() {
  const [tab, setTab] = useState<TabKey>('schemes')
  const [category, setCategory] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const { schemes, loading: schemesLoading, error: schemesError } = useSarkariSchemes()
  const { services, loading: kaamLoading, error: kaamError } = useSarkariKaam()

  const loading = tab === 'schemes' ? schemesLoading : kaamLoading
  const error = tab === 'schemes' ? schemesError : kaamError
  const q = query.trim().toLowerCase()

  const visibleSchemes = schemes
    .filter((s) => !category || s.category === category)
    .filter((s) => !q || s.name.toLowerCase().includes(q))
  const visibleKaam = services.filter((s) => !q || s.title.toLowerCase().includes(q))
  const count = tab === 'schemes' ? visibleSchemes.length : visibleKaam.length

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2">
            Schemes &amp; Sewa
          </h1>
          <p className="font-inter text-muted-foreground text-lg">
            Government schemes and everyday sarkari kaam, in one place
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <button
            onClick={() => { setTab('schemes'); setQuery('') }}
            data-testid="tab-schemes"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-inter text-sm transition-all ${
              tab === 'schemes'
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-card text-foreground hover:bg-primary/5 border'
            }`}
          >
            <PiggyBank className="w-4 h-4" />
            Schemes {!schemesLoading && <span className="text-xs opacity-75">({schemes.length})</span>}
          </button>
          <button
            onClick={() => { setTab('kaam'); setQuery('') }}
            data-testid="tab-kaam"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-inter text-sm transition-all ${
              tab === 'kaam'
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-card text-foreground hover:bg-primary/5 border'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Sarkari Kaam {!kaamLoading && <span className="text-xs opacity-75">({services.length})</span>}
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === 'schemes' ? 'Search schemes...' : 'Search services...'}
            className="pl-9"
            data-testid="input-search-schemes"
          />
        </div>

        {/* Category filter (schemes only) */}
        {tab === 'schemes' && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {CATEGORIES.map((c) => (
              <button
                key={c.label}
                onClick={() => setCategory(c.key)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  category === c.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:bg-primary/5 border'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : error ? (
          <p className="text-center text-destructive py-16">{error}</p>
        ) : count === 0 ? (
          <p className="text-center text-muted-foreground py-16">
            {q ? `Nothing matches "${query}"` : 'Nothing here yet — check back soon.'}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {count} {count === 1 ? 'entry' : 'entries'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tab === 'schemes'
                ? visibleSchemes.map((s) => (
                    <Card key={s.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 flex flex-col h-full">
                        <h3 className="font-inter font-semibold text-foreground mb-2 leading-snug">
                          {s.name}
                        </h3>
                        {s.category && (
                          <Badge
                            variant="secondary"
                            className={`w-fit mb-3 capitalize ${CATEGORY_STYLE[s.category] || ''}`}
                          >
                            {s.category}
                          </Badge>
                        )}
                        {s.url && (
                          <Button size="sm" variant="outline" className="mt-auto w-full" asChild>
                            <a href={s.url} target="_blank" rel="noopener noreferrer">
                              Read more <ExternalLink className="w-3.5 h-3.5 ml-1" />
                            </a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))
                : visibleKaam.map((s) => (
                    <Card key={s.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 flex flex-col h-full">
                        <h3 className="font-inter font-semibold text-foreground mb-3 leading-snug">
                          {s.title}
                        </h3>
                        {s.url && (
                          <Button size="sm" variant="outline" className="mt-auto w-full" asChild>
                            <a href={s.url} target="_blank" rel="noopener noreferrer">
                              Open <ExternalLink className="w-3.5 h-3.5 ml-1" />
                            </a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  )
}
